import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAdminApi } from "@/hooks/useAdminApi";
import { money, errMsg } from "@/lib/api";

const BADGE = { pending: "bg-amber-500/15 text-amber-400", delivered: "bg-emerald-500/15 text-emerald-400", rejected: "bg-red-500/15 text-red-400", awaiting_payment: "bg-sky-500/15 text-sky-400", expired: "bg-gray-500/15 text-gray-400" };
const FILTERS = ["pending", "awaiting_payment", "delivered", "rejected", "expired", ""];

const OrderDialog = ({ order, onClose, onSaved }) => {
  const call = useAdminApi();
  const [codes, setCodes] = useState(order.codes || "");
  const [note, setNote] = useState(order.admin_note || "");
  const save = async (status) => {
    try {
      const r = await call("patch", `/admin/orders/${order.id}`, { status, codes, admin_note: note });
      toast.success(`Order ${r.id} marked ${status}`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const area = "w-full rounded-md bg-[#08090C] border border-white/10 p-3 text-sm focus:outline-none focus:border-emerald-500/60";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="order-dialog" className="bg-[#0F1117] border-white/10 text-white max-w-lg w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader><DialogTitle className="font-mono">{order.id}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><p className="label-eyebrow text-gray-500">Email</p><p className="mt-1 break-all">{order.email}</p></div>
            <div><p className="label-eyebrow text-gray-500">Amount</p><p className="mt-1 font-mono">{money(order.total)} · {order.network}</p></div>
          </div>
          <div>
            <p className="label-eyebrow text-gray-500">TX hash</p>
            <div className="flex gap-2 mt-1 items-start">
              <code className="font-mono text-xs break-all flex-1">{order.tx_hash || "— not submitted —"}</code>
              <button onClick={() => { navigator.clipboard.writeText(order.tx_hash); toast.success("Copied"); }} className="p-2 -m-1 text-gray-400 hover:text-emerald-400"><Copy size={16} /></button>
            </div>
          </div>
          <div className="space-y-1">{order.items.map((i) => <p key={i.game_id}>{i.name} <span className="text-gray-500">({i.platform}) × {i.qty}</span></p>)}</div>
          <label className="block space-y-1"><span className="text-gray-400">Activation codes sent (for your records)</span>
            <textarea data-testid="order-codes-input" rows={3} value={codes} onChange={(e) => setCodes(e.target.value)} className={`${area} font-mono`} /></label>
          <label className="block space-y-1"><span className="text-gray-400">Note shown to customer (optional)</span>
            <input data-testid="order-note-input" value={note} onChange={(e) => setNote(e.target.value)} className={area} /></label>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 pt-2">
            <button data-testid="admin-verify-order-button" onClick={() => save("delivered")} className="h-10 px-4 rounded-md bg-emerald-500 text-[#08090C] font-bold hover:bg-emerald-400">Mark delivered</button>
            <button data-testid="admin-reject-order-button" onClick={() => save("rejected")} className="h-10 px-4 rounded-md border border-red-500/40 text-red-400 hover:bg-red-500/10">Reject</button>
            <button data-testid="admin-ban-customer-button" onClick={async () => {
              if (!window.confirm(`Ban ${order.email} for a fake transaction hash? The order will be rejected.`)) return;
              try { await call("post", "/admin/bans", { order_id: order.id }); toast.success("Customer banned and order rejected"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
            }} className="h-10 px-4 rounded-md bg-red-600 text-white font-bold hover:bg-red-500">Ban (fake hash)</button>
            <button data-testid="admin-pending-order-button" onClick={() => save("pending")} className="h-10 px-4 rounded-md border border-white/10 text-gray-300 hover:bg-white/5">Back to pending</button>
          </div>
          <p className="text-xs text-gray-500">Send the code to the customer's email yourself, then mark the order delivered. Rejecting returns the items to stock.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const OrdersTab = ({ onChange }) => {
  const call = useAdminApi();
  const [filter, setFilter] = useState("pending");
  const [orders, setOrders] = useState(null);
  const [active, setActive] = useState(null);
  const load = () => call("get", "/admin/orders", null, { params: filter ? { status: filter } : {} }).then(setOrders);
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <div className="flex flex-nowrap sm:flex-wrap overflow-x-auto no-scrollbar gap-2">
        {FILTERS.map((f) => (
          <button key={f || "all"} data-testid={`orders-filter-${f || "all"}`} onClick={() => setFilter(f)}
            className={`shrink-0 h-10 sm:h-8 px-3 rounded text-sm capitalize whitespace-nowrap ${filter === f ? "bg-white/10 text-white" : "text-gray-400 hover:text-white"}`}>{(f || "all").replace("_", " ")}</button>
        ))}
      </div>
      <div data-testid="orders-mobile-list" className="md:hidden space-y-3">
        {orders?.map((o) => (
          <button key={o.id} data-testid={`order-card-${o.id}`} onClick={() => setActive(o)} className="w-full text-left rounded-lg border border-white/10 bg-[#0F1117] p-4 space-y-2 active:bg-white/[0.04]">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono font-semibold">{o.id}</span>
              <span className={`px-2 py-1 rounded text-xs font-semibold capitalize whitespace-nowrap ${BADGE[o.status]}`}>{o.status.replace("_", " ")}</span>
            </div>
            <p className="text-sm text-gray-300 truncate">{o.email}</p>
            <p className="text-xs text-gray-500 line-clamp-2">{o.items.map((i) => `${i.name}×${i.qty}`).join(", ")}</p>
            <div className="flex items-center justify-between text-sm">
              <span className="font-mono font-bold text-emerald-400">{money(o.total)} <span className="text-gray-500 font-normal">· {o.network}</span></span>
              <span className="text-xs text-gray-500">{new Date(o.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
            </div>
          </button>
        ))}
        {orders?.length === 0 && <p data-testid="orders-empty-mobile" className="p-10 text-center text-gray-500 rounded-lg border border-white/10">No orders here yet.</p>}
      </div>
      <div className="hidden md:block rounded-lg border border-white/10 bg-[#0F1117] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-gray-500 label-eyebrow"><tr>{["Order", "Email", "Items", "Total", "Network", "Status", "Date"].map((h) => <th key={h} className="p-4 font-semibold">{h}</th>)}</tr></thead>
          <tbody>
            {orders?.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.id}`} onClick={() => setActive(o)} className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer">
                <td className="p-4 font-mono">{o.id}</td>
                <td className="p-4">{o.email}</td>
                <td className="p-4 text-gray-300">{o.items.map((i) => `${i.name}×${i.qty}`).join(", ")}</td>
                <td className="p-4 font-mono">{money(o.total)}</td>
                <td className="p-4 font-mono">{o.network}</td>
                <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-semibold capitalize whitespace-nowrap ${BADGE[o.status]}`}>{o.status.replace("_", " ")}</span></td>
                <td className="p-4 text-gray-500 whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders?.length === 0 && <p data-testid="orders-empty" className="p-10 text-center text-gray-500">No orders here yet.</p>}
      </div>
      {active && <OrderDialog order={active} onClose={() => setActive(null)} onSaved={() => { setActive(null); load(); onChange(); }} />}
    </div>
  );
};
